import { Fragment, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../lib/api'
import { useAuth } from '../lib/auth'
import { floorLabel, isUnderstructure } from '../lib/floors'

/**
 * The tower map — the home screen and the spine of the whole game.
 *
 * Floors render top-down (14 at the top, 1 at the bottom) because you climb
 * upward, and a progress list that runs the wrong way feels wrong in a way
 * people can't articulate but do abandon.
 *
 * Below floor 1 sits the Understructure: B1 JavaScript and B2 Python, stored as
 * floors -1 and -2. The existing descending sort put them in the right place
 * for free; all this file adds is a ground line and a different card treatment,
 * so they read as foundation rather than as floors you failed to reach.
 */

interface ModuleRow {
  id: string
  slug: string
  floor: number
  title: string
  zone: string
  boss: string
  summary: string
  color: string
  released: number
  lesson_count: number
}

export default function Tower() {
  const { user, game } = useAuth()
  const [modules, setModules] = useState<ModuleRow[]>([])
  const [progress, setProgress] = useState<Record<string, number>>({})
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const controller = new AbortController()
    api<{ modules: ModuleRow[]; progress: Record<string, number> }>('/modules', {
      signal: controller.signal,
    })
      .then((data) => {
        setModules(data.modules)
        setProgress(data.progress ?? {})
      })
      .catch((e: unknown) => {
        if ((e as Error).name !== 'AbortError') setError((e as Error).message)
      })
      .finally(() => setLoading(false))
    return () => controller.abort()
  }, [user])

  const sorted = [...modules].sort((a, b) => b.floor - a.floor)

  return (
    <div className="tower">
      <section className="hero">
        <h1>The Stack</h1>
        <p className="hero-sub">
          Fourteen floors, from a socket listening on a port to a language model citing its
          sources — and two below them for the languages it is all written in. Every floor is
          demonstrated by the site you are standing on.
        </p>
        {!user && (
          <p className="hero-cta">
            <Link to="/login" className="button button-primary">
              Start climbing
            </Link>
            <span className="dim"> — free, and the first account becomes the owner.</span>
          </p>
        )}
        {user && game && (
          <p className="hero-cta">
            <strong>Level {game.level}</strong> · {game.totalXp} XP · 🔥 {game.streak} day streak
          </p>
        )}
      </section>

      {loading && <div className="loading">Loading the tower…</div>}
      {error && (
        <div className="error-panel">
          <strong>Could not load the tower.</strong> {error}
          <p className="dim">
            If this is a fresh checkout, the database may not be seeded yet — run{' '}
            <code>npm run seed</code>.
          </p>
        </div>
      )}

      <ol className="floors">
        {sorted.map((m, i) => {
          const done = progress[m.id] ?? 0
          const pct = m.lesson_count > 0 ? Math.round((done / m.lesson_count) * 100) : 0
          const locked = m.released === 0
          const below = isUnderstructure(m.floor)
          // The ground line: drawn once, at the first floor below zero. Sorting
          // is descending, so this is the boundary between the tower and what
          // holds it up.
          const isFirstBelow = below && !isUnderstructure(sorted[i - 1]?.floor ?? 1)

          return (
            <Fragment key={m.id}>
              {isFirstBelow && (
                <li className="ground-line" aria-hidden="true">
                  <span>ground level</span>
                </li>
              )}
              <li
                className={[
                  'floor',
                  locked ? 'floor-locked' : '',
                  below ? 'floor-below' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
              <div className="floor-number" style={{ borderColor: m.color }}>
                <span>{floorLabel(m.floor)}</span>
              </div>

              <div className="floor-body">
                <div className="floor-head">
                  <h2>
                    {locked ? (
                      <span className="floor-title">{m.zone}</span>
                    ) : (
                      <Link to={`/floor/${m.slug}`} className="floor-title">
                        {m.zone}
                      </Link>
                    )}
                    <span className="floor-subject">{m.title}</span>
                    {below && <span className="optional-tag">optional · always open</span>}
                  </h2>
                  {locked ? (
                    <span className="lock" title="Not built yet">
                      🔒
                    </span>
                  ) : (
                    <span className="boss-tag" title="Floor boss">
                      ⚔ {m.boss}
                    </span>
                  )}
                </div>

                <p className="floor-summary">{m.summary}</p>

                {!locked && (
                  <div className="floor-progress">
                    <div className="progress-track">
                      <div
                        className="progress-fill"
                        style={{ width: `${pct}%`, background: m.color }}
                      />
                    </div>
                    <span className="dim">
                      {done} / {m.lesson_count} lessons
                    </span>
                  </div>
                )}
              </div>
              </li>
            </Fragment>
          )
        })}
      </ol>
    </div>
  )
}
